<template>
  <div
    v-if="isImagesExist"
    class="gallery"
    :class="galleryClass"
  >
    <div
      ref="slidesWrapper"
      class="gallery__slides"
      :style="cssVariables"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { TImageSize } from '~/types/common';

  export default defineNuxtComponent({
    name: 'GalleryGrid',

    props: {
      images: {
        type: Array as PropType<Array<TImageSize[]>>,
        required: true,
      },

      columns: {
        type: Number as PropType<number>,
        default: 3,
      },
    },

    data: () => ({
      // в мобильной версии всегда 2 колонки
      // пока нет возможности ограничить на бэке
      // количество колонок
      mobileColumns: 2,
    }),

    computed: {
      desktopRemainder(): number {
        const reminder = this.images.length % this.columns;
        return reminder === 0 ? this.columns : reminder;
      },

      mobileRemainder(): number {
        const reminder = this.images.length % this.mobileColumns;
        return reminder === 0 ? this.mobileColumns : reminder;
      },

      isImagesExist(): boolean {
        return this.images.length > 1;
      },

      imagesWithoutLastGroup(): Array<TImageSize[]> {
        const remainderNumber = this.desktopRemainder === this.columns ? 0 : this.desktopRemainder;
        const imagesLength = this.images.length - remainderNumber;
        return this.images.slice(0, imagesLength);
      },

      sortedImages(): Array<TImageSize[]> {
        return [...this.imagesWithoutLastGroup].sort((a, b) => a[0].height - b[0].height);
      },

      lastGroupMinSlide(): TImageSize {
        if (this.lastGroup.length > 0) {
          const lastGroup = [...this.lastGroup];
          lastGroup.sort((a, b) => a[0].height - b[0].height);

          return lastGroup[0][0];
        }
        return this.minSlide;
      },

      minSlide(): TImageSize {
        return this.sortedImages[0][0];
      },

      minSlideAspectRatio(): number {
        return this.minSlide.width / this.minSlide.height;
      },

      lastSlideAspectRatio(): number {
        return this.lastGroupMinSlide.width / this.lastGroupMinSlide.height;
      },

      lastGroup(): Array<TImageSize[]> {
        return this.images.slice(this.imagesWithoutLastGroup.length);
      },

      galleryClass(): Record<string, any> {
        const className = 'gallery';
        return {
          [`${className}--columns-${this.columns}`]: true,
          [`${className}--mobile-remainder-${this.mobileRemainder}`]: true,
          [`${className}--desktop-remainder-${this.desktopRemainder}`]: true,
        };
      },

      cssVariables(): string {
        return `
          --last-slide-aspect-ratio: ${this.lastSlideAspectRatio};
          --any-slide-aspect-ratio: ${this.minSlideAspectRatio};
        `;
      },
    },

    methods: {
      openImage(url: string) {
        this.$emit('open-image', url);
      },
    },
  });
</script>

<!-- из-за scoped тут все разваливается -->
<style lang="scss" src="@/scss/gallery/gallery.scss" />
